import {
  put,
  takeLatest,
  takeEvery,
  takeLeading, select,
  all,
  call,
} from 'redux-saga/effects';
import history from 'app/routes/history';
import {
  TeammoApi,
  normalize,
  // compilePaginationObject,
} from 'app/utils/redux/helpers';
import { handleError } from 'app/utils/handlers/handleError';
import {
  getJwtToken,
  isAuthenticated,
} from 'app/ducks/authentication/selectors';
import { mobileDeviceTokenSchema } from 'app/utils/redux/schemas';
import _ from 'lodash';
import {
  CognitoLogin,
  CognitoLogout,
  CognitoForgotPassword,
  CognitoChangePassword,
} from 'app/utils/aws/awsCognito';
import jwtDecode from 'jwt-decode';
import { getUser } from 'app/ducks/users/actions';
import { sagaActions } from 'app/ducks/authentication/actions';
import { globalLoad } from 'app/ducks/global/actions';

import { AuthenticationActionTypes as types } from 'app/ducks/authentication/types';

function* refreshUserSaga(action): any {
  yield put(sagaActions.refreshUserSuccess(action.payload));
}

function* authenticateCognitoUserSaga(action): any {
  yield put(sagaActions.authenticateCognitoUserPending());
  try {
    const response = yield call(CognitoLogin, {
      email: action.payload.user.email.toLowerCase(),
      password: action.payload.user.password,
    });
    const { jwtToken } = response.signInUserSession.idToken;
    const id = jwtDecode(jwtToken).sub;
    const redirectLocation = yield select(
      (state) => state.authentication.redirectLocation,
    );
    const redirect = redirectLocation !== undefined;

    if (redirectLocation !== '/dashboard') {
      history.push(redirectLocation);
    } else {
      history.push('/dashboard');
    }
    yield put(
      sagaActions.authenticateCognitoUserSuccess(response.signInUserSession),
    );
    if (action.payload.findUser) {
      yield put(getUser(id, !redirect));
    }
  } catch (error) {
    yield put(sagaActions.authenticateCognitoUserFailed(error.message));
  }
}

function* forgotPasswordSaga(action): any {
  yield put(sagaActions.forgotPasswordPending());
  try {
    yield call(CognitoForgotPassword, {
      email: action.payload.user.email.toLowerCase(),
    });
    yield put(
      sagaActions.forgotPasswordSuccess({
        email: action.payload.user.email.toLowerCase(),
      }),
    );
    history.push('/forgot-password/confirm');
  } catch (error) {
    yield put(sagaActions.forgotPasswordFailed(error.message));
  }
}

function* changePasswordSaga(action): any {
  yield put(sagaActions.changePasswordPending());
  try {
    const response = yield call(CognitoChangePassword, {
      email: action.payload.user.email.toLowerCase(),
      password: action.payload.user.password,
      verificationCode: action.payload.user.verificationCode,
    });
    if (action.payload.callback) {
      action.payload.callback();
    }
    yield put(sagaActions.changePasswordSuccess(response));
  } catch (error) {
    yield put(sagaActions.changePasswordFailed(error.message));
  }
}

function* logoutUserSaga(action): any {
  CognitoLogout();
  if (action.payload.redirect) {
    history.push('/login');
  }
  yield put(
    sagaActions.userLogout({
      redirectLocation: action.payload.redirectLocation,
    }),
  );
}

function* refreshOrLogoutUserSaga(action): any {
  yield put(sagaActions.authenticateCognitoUserPending());

  const refreshSuccessful = yield isAuthenticated();
  if (!refreshSuccessful) {
    CognitoLogout();

    yield put(
      sagaActions.authenticateCognitoUserFailed('Please login to continue.'),
    );
    if (action.payload.redirect) {
      history.push('/login');
    }
    yield put(
      sagaActions.userLogout({
        redirectLocation: action.payload.redirectLocation,
      }),
    );
  } else {
    yield put(sagaActions.authenticateCognitoUserSuccess('Logged in.'));
  }
}

function* registerMobileDeviceTokenSaga(action): any {
  // const authToken = getJwtToken();
  // console.log('Logging to show function works!');
  // console.log('Auth token');
  // console.log(authToken);
  // console.log('Device token');
  // console.log(action.payload.token);
  // console.log('Device type');
  // console.log(action.payload.deviceType);
  yield put(sagaActions.registerMobileDeviceTokenPending());

  const [error, response] = yield (yield yield TeammoApi(
    yield select(),
  )).mobileDeviceTokens.create({
    deviceType: action.payload.deviceType,
    token: action.payload.token,
  });
  if (response) {
    const normalizedResponse = normalize(response, mobileDeviceTokenSchema);

    yield put(sagaActions.registerMobileDeviceTokenSuccess(normalizedResponse));
    return normalizedResponse.entities.mobileDeviceTokens[
      normalizedResponse.result
    ];
  }
  yield put(
    handleError(
      error,
      sagaActions.registerMobileDeviceTokenFailed(
        _.isEmpty(error) ? {} : error.response.data.errors,
      ),
    ),
  );
  return null;
}

function* switchUserSaga(action): any {
  // console.log(action);
  yield put(globalLoad({}));
}

/* ********************************* SAGA WATCHERS ********************************* */

function* refreshUserSagaWatcher(): any {
  yield takeLatest(types.REFRESH_COGNITO_USER_REQUESTED, refreshUserSaga);
}

function* authenticateCognitoUserSagaWatcher(): any {
  yield takeLatest(
    types.AUTHENTICATE_COGNITO_USER_REQUESTED,
    authenticateCognitoUserSaga,
  );
}

function* forgotPasswordSagaWatcher(): any {
  yield takeLatest(types.FORGOT_PASSWORD_REQUESTED, forgotPasswordSaga);
}
function* changePasswordSagaWatcher(): any {
  yield takeLatest(types.CHANGE_PASSWORD_REQUESTED, changePasswordSaga);
}

function* logoutUserSagaWatcher(): any {
  yield takeLatest(types.USER_LOGOUT_REQUESTED, logoutUserSaga);
}
function* registerMobileDeviceTokenSagaWatcher(): any {
  yield takeLatest(
    types.REGISTER_MOBILE_DEVICE_TOKEN_REQUESTED,
    registerMobileDeviceTokenSaga,
  );
}
function* refreshOrLogoutUserSagaWatcher(): any {
  yield takeLeading(types.REFRESH_OR_LOGOUT_REQUESTED, refreshOrLogoutUserSaga);
}

function* switchUserSagaWatcher(): any {
  yield takeLatest(types.SWITCH_USER, switchUserSaga);
}

export default function* root(): any {
  yield all([
    refreshUserSagaWatcher(),
    authenticateCognitoUserSagaWatcher(),
    forgotPasswordSagaWatcher(),
    changePasswordSagaWatcher(),
    logoutUserSagaWatcher(),
    registerMobileDeviceTokenSagaWatcher(),
    refreshOrLogoutUserSagaWatcher(),
    switchUserSagaWatcher(),
  ]);
}
